import { Component, Suspense, lazy, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from './store/authStore.js';
import { useSocket } from './hooks/useSocket.js';
import { paymentsApi } from './api/payments.api.js';
import LandingPage from './pages/LandingPage.jsx';
import { ThemeProvider, useTheme } from './contexts/ThemeContext.jsx';
import { TopBar } from './components/layout/TopBar.jsx';
import { MobileNav } from './components/layout/MobileNav.jsx';
import { canOpen, defaultWorkspacePath } from './utils/navigation.js';
import { money } from './utils/planQuote.js';
import { appUrl, isAppHost, isPublicSiteHost } from './utils/publicUrls.js';
import { loadPaddleSdk, paddleCheckoutItems } from './utils/paddleCheckout.js';
import {
  hasAutomation,
  hasCalls,
  hasCampaigns,
  hasInbox,
  hasShortLinks,
} from './utils/tenantFeatures.js';

const Login = lazy(() => import('./pages/Login.jsx'));
const InviteSignup = lazy(() => import('./pages/InviteSignup.jsx'));
const MobileAppDownload = lazy(() => import('./pages/MobileAppDownload.jsx'));
const LegalPage = lazy(() => import('./pages/LegalPages.jsx'));
const ServicesIndex = lazy(() => import('./pages/MarketingPages.jsx').then((module) => ({ default: module.ServicesIndex })));
const ServicePage = lazy(() => import('./pages/MarketingPages.jsx').then((module) => ({ default: module.ServicePage })));
const BlogIndex = lazy(() => import('./pages/MarketingPages.jsx').then((module) => ({ default: module.BlogIndex })));
const BlogPost = lazy(() => import('./pages/MarketingPages.jsx').then((module) => ({ default: module.BlogPost })));
const Welcome = lazy(() => import('./pages/Welcome.jsx'));
const Sidebar = lazy(() => import('./components/layout/Sidebar.jsx').then((module) => ({ default: module.Sidebar })));
const Dashboard = lazy(() => import('./pages/Dashboard.jsx'));
const Inbox = lazy(() => import('./pages/Inbox.jsx'));
const ContactProfile = lazy(() => import('./pages/ContactProfile.jsx'));
const Channels = lazy(() => import('./pages/Channels.jsx'));
const ShortLinks = lazy(() => import('./pages/ShortLinks.jsx'));
const Reports = lazy(() => import('./pages/Reports.jsx'));
const CampaignDetail = lazy(() => import('./pages/CampaignDetail.jsx'));
const CampaignWizard = lazy(() => import('./pages/CampaignWizard.jsx'));
const PlatformDashboard = lazy(() => import('./pages/PlatformDashboard.jsx'));
const Contacts = lazy(() => import('./pages/Contacts.jsx'));
const Settings = lazy(() => import('./pages/Settings.jsx'));
const Automations = lazy(() => import('./pages/Automations.jsx'));
const AutomationBuilder = lazy(() => import('./pages/AutomationBuilder.jsx'));
const Campaigns = lazy(() => import('./pages/Campaigns.jsx'));
const Calls = lazy(() => import('./pages/Calls.jsx'));
const AskAiPanel = lazy(() => import('./components/ai/AskAiPanel.jsx').then((module) => ({ default: module.AskAiPanel })));
const Softphone = lazy(() => import('./components/calls/Softphone.jsx').then((module) => ({ default: module.Softphone })));
const InstallAppPrompt = lazy(() => import('./components/shared/InstallAppPrompt.jsx').then((module) => ({ default: module.InstallAppPrompt })));

const LEGAL_PATHS = ['/privacy', '/terms', '/refund-policy', '/acceptable-use', '/sms-terms'];

function isLegacyRedAccent(color = '') {
  const normalized = String(color).trim().toUpperCase();
  return ['#EF1111', '#E5092C', '#D60024', '#B60020', '#FF2A45', '#A6001B', '#073F2D', '#0A553C', '#C9FF55'].includes(normalized);
}

function billingBlocked(tenant) {
  if (!tenant) return false;
  if (tenant.billingStatus === 'PAST_DUE') return true;
  if (tenant.billingStatus === 'TRIALING' && tenant.currentPeriodEndsAt) {
    const endsAt = new Date(tenant.currentPeriodEndsAt);
    return !Number.isNaN(endsAt.getTime()) && endsAt < new Date();
  }
  if (tenant.billingStatus === 'PAID' && tenant.accessType !== 'LIFETIME' && tenant.currentPeriodEndsAt) {
    const endsAt = new Date(tenant.currentPeriodEndsAt);
    return !Number.isNaN(endsAt.getTime()) && endsAt < new Date();
  }
  return false;
}

function PaymentRequiredScreen() {
  const user = useAuthStore(s => s.user);
  const tenant = useAuthStore(s => s.tenant);
  const setTenant = useAuthStore(s => s.setTenant);
  const logout = useAuthStore(s => s.logout);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    let cancelled = false;
    paymentsApi.getConfig()
      .then(({ data }) => {
        if (!cancelled) setConfig(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.message || 'Could not load payment setup');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openCheckout = async () => {
    try {
      setError('');
      const paddleConfig = config?.paddle;
      const items = paddleCheckoutItems(paddleConfig?.items);
      if (!paddleConfig?.enabled || !items.length) throw new Error(paddleConfig?.error || 'Paddle checkout is not configured yet');
      const Paddle = await loadPaddleSdk({
        clientToken: paddleConfig.clientToken,
        environment: paddleConfig.environment,
        eventCallback: async (event) => {
          if (event?.name !== 'checkout.completed') return;
          try {
            const transactionId = event?.data?.transaction_id || event?.data?.transactionId;
            if (!transactionId) return toast.error('Paddle did not return a transaction ID');
            const response = await paymentsApi.completePaddleCheckout(transactionId, {
              features: workspace.features,
              billingPeriod: workspace.billingPeriod,
            });
            if (response.data?.tenant) setTenant({ ...(tenant || {}), ...response.data.tenant });
            setPaid(true);
            toast.success('Payment received. Workspace is active.');
            window.location.href = defaultWorkspacePath({ ...(tenant || {}), ...(response.data?.tenant || {}) }, user?.role);
          } catch (err) {
            const message = err.response?.data?.message || err.message || 'Payment was received, but activation could not be confirmed yet';
            setError(message);
            toast.error(message);
          }
        },
      });
      Paddle.Checkout.open({
        items,
        ...(user?.email || tenant?.ownerEmail ? { customer: { email: user?.email || tenant?.ownerEmail } } : {}),
        customData: {
          tenantId: workspace.id,
          workspace: workspace.name,
          features: workspace.features,
          billingPeriod: workspace.billingPeriod,
        },
        settings: {
          displayMode: 'overlay',
          variant: 'one-page',
          theme: 'light',
          locale: 'en',
          successUrl: appUrl('/welcome'),
        },
      });
    } catch (err) {
      setError(err.message || 'Could not open Paddle checkout');
    }
  };

  const workspace = config?.workspace || tenant || {};
  const amount = workspace.amountCents || workspace.monthlyPriceCents || workspace.lifetimePriceCents || 0;
  const pendingTrialActivation = workspace.billingStatus === 'PAST_DUE' && !workspace.paddleSubscriptionId;
  const period = workspace.accessType === 'LIFETIME' ? 'one-time lifetime access' : 'next 30 days';

  return (
    <div className="min-h-dvh flex items-center justify-center p-5" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <div className="w-full max-w-3xl rounded-[28px] border p-6 shadow-2xl" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[.22em]" style={{ color: 'var(--accent)' }}>{pendingTrialActivation ? 'Trial Activation' : 'Payment Required'}</p>
            <h1 className="mt-2 text-3xl font-bold">{pendingTrialActivation ? 'Start your 7-day trial' : 'Keep your workspace active'}</h1>
            <p className="mt-2 text-sm leading-relaxed" style={{ color: 'var(--textMuted)' }}>
              {pendingTrialActivation
                ? 'Complete secure Paddle checkout to activate this workspace. The first charge happens after the 7-day trial.'
                : 'Your trial or billing period has ended. You can still sign in here to pay, but conversations, campaigns, calls, WhatsApp API, and automations stay locked until payment is confirmed.'}
            </p>
          </div>
          <button type="button" className="btn-secondary whitespace-nowrap" onClick={logout}>Sign out</button>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_auto]">
          <div className="rounded-2xl border p-4" style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--textDim)' }}>Workspace</p>
            <p className="mt-1 text-xl font-bold">{workspace.planName || tenant?.planName || 'Workspace access'}</p>
            <p className="mt-1 text-sm" style={{ color: 'var(--textMuted)' }}>{tenant?.name || workspace.name}</p>
          </div>
          <div className="rounded-2xl border p-4 min-w-44" style={{ borderColor: 'var(--border)', background: 'var(--accentSoft)' }}>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--textDim)' }}>{pendingTrialActivation ? 'Today' : 'Due now'}</p>
            <p className="mt-1 text-4xl font-bold" style={{ color: 'var(--accent)' }}>{pendingTrialActivation ? money(0, config?.paddle?.currency || 'USD') : money(amount, config?.paddle?.currency || 'USD')}</p>
            <p className="mt-1 text-xs font-bold" style={{ color: 'var(--textMuted)' }}>{pendingTrialActivation ? `${money(amount, config?.paddle?.currency || 'USD')} after trial` : period}</p>
          </div>
        </div>

        <div className="mt-5">
          {loading && <p className="text-sm" style={{ color: 'var(--textMuted)' }}>Loading secure checkout...</p>}
          {!loading && !config?.paddle?.enabled && (
            <div className="rounded-2xl border p-4" style={{ borderColor: 'var(--warning)', background: 'rgba(245,166,35,.08)' }}>
              <p className="font-bold">Payment is not configured yet</p>
              <p className="mt-1 text-sm" style={{ color: 'var(--textMuted)' }}>Contact support to activate this workspace manually.</p>
            </div>
          )}
          {!loading && config?.paddle?.enabled && (
            <button type="button" onClick={openCheckout} className="btn-primary w-full justify-center">
              {pendingTrialActivation ? 'Activate trial with Paddle' : 'Open secure checkout'}
            </button>
          )}
          {error && <p className="mt-3 text-sm font-bold" style={{ color: 'var(--danger)' }}>{error}</p>}
        </div>
      </div>
    </div>
  );
}

function ProtectedLayout() {
  const user = useAuthStore(s => s.user);
  const tenant = useAuthStore(s => s.tenant);
  const role = user?.role;
  const staff = ['ADMIN', 'MANAGER'].includes(role);
  useSocket();
  if (role === 'PLATFORM_ADMIN') {
    return (
      <div className="flex h-dvh overflow-hidden">
        <div className="hidden md:flex"><Suspense fallback={null}><Sidebar /></Suspense></div>
        <main className="app-content flex-1 min-w-0 overflow-hidden">
          <Suspense fallback={<AppLoading />}>
            <Routes>
              <Route path="/dashboard" element={<PlatformDashboard />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
        </main>
        <MobileNav />
        <Suspense fallback={null}><InstallAppPrompt /></Suspense>
      </div>
    );
  }
  if (billingBlocked(tenant)) return <PaymentRequiredScreen />;
  const home = defaultWorkspacePath(tenant, role);
  return (
    <div className="flex h-dvh overflow-hidden">
      <div className="hidden md:flex">
        <Suspense fallback={null}><Sidebar /></Suspense>
      </div>
      <main className="app-content flex-1 min-w-0 flex flex-col overflow-hidden">
        <TopBar />
        <div className="flex-1 min-h-0 overflow-hidden">
          <Suspense fallback={<AppLoading />}>
            <Routes>
              <Route path="/dashboard" element={<Dashboard />} />
              {hasInbox(tenant) && <Route path="/inbox" element={<Inbox />} />}
              <Route path="/contacts" element={<Contacts />} />
              <Route path="/contacts/:id" element={<ContactProfile />} />
              {hasCalls(tenant) && <Route path="/calls" element={<Calls />} />}
              {staff && hasCampaigns(tenant) && <Route path="/campaigns" element={<Campaigns />} />}
              {staff && hasCampaigns(tenant) && <Route path="/campaigns/new" element={<CampaignWizard />} />}
              {staff && hasCampaigns(tenant) && <Route path="/campaigns/:id" element={<CampaignDetail />} />}
              {staff && hasAutomation(tenant) && <Route path="/automations" element={<Automations />} />}
              {staff && hasAutomation(tenant) && <Route path="/automations/:id" element={<AutomationBuilder />} />}
              {staff && hasShortLinks(tenant) && <Route path="/links" element={<ShortLinks />} />}
              {staff && <Route path="/reports" element={<Reports />} />}
              {role === 'ADMIN' && <Route path="/channels" element={<Channels />} />}
              {role === 'ADMIN' && <Route path="/channels/:channel" element={<Channels />} />}
              {role === 'ADMIN' && <Route path="/settings" element={<Settings />} />}
              <Route path="/whatsapp" element={<Navigate to={canOpen('/channels', tenant, role) ? '/channels/whatsapp' : home} replace />} />
              <Route path="*" element={<Navigate to={home} replace />} />
            </Routes>
          </Suspense>
        </div>
      </main>
      <MobileNav />
      <Suspense fallback={null}>
        {hasAutomation(tenant) && tenant?.askAiBubbleEnabled !== false && staff && <AskAiPanel />}
        {hasCalls(tenant) && ['ADMIN', 'MANAGER', 'AGENT'].includes(role) && <Softphone enabled />}
      </Suspense>
      <Suspense fallback={null}><InstallAppPrompt /></Suspense>
    </div>
  );
}

function AuthGuard({ children }) {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated());
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function WorkspaceBrandSync() {
  const tenant = useAuthStore(s => s.tenant);
  const { updateTheme } = useTheme();

  useEffect(() => {
    if (tenant?.primaryColor && !isLegacyRedAccent(tenant.primaryColor)) {
      updateTheme({ customAccent: tenant.primaryColor });
    } else {
      updateTheme({ customAccent: '' });
    }
    if (tenant?.brandName || tenant?.name) {
      document.title = tenant.brandName || tenant.name;
    }
  }, [tenant?.primaryColor, tenant?.brandName, tenant?.name]);

  return null;
}

function AppLoading() {
  return (
    <div className="min-h-dvh flex items-center justify-center" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <div className="text-center">
        <div className="noir-brand-mark w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 text-xl font-bold">
          I
        </div>
        <p className="text-sm font-semibold" style={{ color: 'var(--textMuted)' }}>Loading workspace...</p>
      </div>
    </div>
  );
}

function PublicPage({ children }) {
  return (
    <div
      className="h-full overflow-y-auto overflow-x-hidden"
      style={{
        background: '#F5F7FB',
        scrollbarColor: '#D3D9E6 transparent',
      }}
    >
      {children}
    </div>
  );
}

class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  async clearAppCaches() {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
    } catch {}
  }

  refreshApp = async () => {
    await this.clearAppCaches();
    window.location.reload();
  };

  signInAgain = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch {}
    try {
      useAuthStore.getState().clearSession();
    } catch {}
    await this.clearAppCaches();
    window.location.assign('/login');
  };

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
          <div className="card max-w-md w-full p-5" style={{ borderColor: 'var(--borderHi)' }}>
            <div className="noir-brand-mark w-12 h-12 rounded-xl flex items-center justify-center mb-4 text-lg font-bold">
              I
            </div>
            <h1 className="text-xl font-bold mb-2">Workspace needs a refresh</h1>
            <p className="text-sm mb-4" style={{ color: 'var(--textMuted)' }}>
              The app hit a frontend error, usually because the phone still has an old app bundle cached after an update.
            </p>
            <div className="grid gap-2">
              <button type="button" className="btn-primary w-full" onClick={this.refreshApp}>
                Refresh App
              </button>
              <button type="button" className="btn-secondary w-full" onClick={this.signInAgain}>
                Sign In Again
              </button>
            </div>
            <p className="text-xs mt-3" style={{ color: 'var(--textDim)' }}>
              Refresh clears the cached app shell. Sign In Again also clears the current session.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function App() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated());
  const tenant = useAuthStore(s => s.tenant);
  const user = useAuthStore(s => s.user);
  const fetchMe = useAuthStore(s => s.fetchMe);
  const [isCheckingAuth, setIsCheckingAuth] = useState(!isAuthenticated);
  const onAppHost = isAppHost();

  useEffect(() => {
    let mounted = true;
    const path = window.location.pathname;
    const isLegalPath = LEGAL_PATHS.includes(path);
    const isMarketingPath = path === '/services' || path.startsWith('/services/') || path === '/blog' || path.startsWith('/blog/');
    if (isPublicSiteHost() && !['/', '/mobile-app', ...LEGAL_PATHS].includes(path) && !isMarketingPath) {
      window.location.replace(appUrl(`${path}${window.location.search}${window.location.hash}`));
      return () => {
        mounted = false;
      };
    }
    const publicRoute = path === '/' || path === '/login' || path === '/mobile-app' || isLegalPath || isMarketingPath || path.startsWith('/signup/');
    if (!isAuthenticated && publicRoute) {
      setIsCheckingAuth(false);
      return () => {
        mounted = false;
      };
    }
    const fallback = setTimeout(() => {
      if (mounted) setIsCheckingAuth(false);
    }, 3500);

    async function boot() {
      try {
        if (!isAuthenticated) {
          await fetchMe();
        }
      } finally {
        clearTimeout(fallback);
        if (mounted) setIsCheckingAuth(false);
      }
    }
    boot();
    return () => {
      mounted = false;
      clearTimeout(fallback);
    };
  }, [fetchMe, isAuthenticated]);

  return (
    <ThemeProvider>
      <AppErrorBoundary>
        <WorkspaceBrandSync />
        {isCheckingAuth ? (
          <AppLoading />
        ) : (
          <Routes>
            <Route path="/mobile-app" element={<PublicPage><Suspense fallback={<AppLoading />}><MobileAppDownload /></Suspense></PublicPage>} />
            <Route path="/services" element={<PublicPage><Suspense fallback={<AppLoading />}><ServicesIndex /></Suspense></PublicPage>} />
            <Route path="/services/:slug" element={<PublicPage><Suspense fallback={<AppLoading />}><ServicePage /></Suspense></PublicPage>} />
            <Route path="/blog" element={<PublicPage><Suspense fallback={<AppLoading />}><BlogIndex /></Suspense></PublicPage>} />
            <Route path="/blog/:slug" element={<PublicPage><Suspense fallback={<AppLoading />}><BlogPost /></Suspense></PublicPage>} />
            <Route path="/privacy" element={<PublicPage><Suspense fallback={<AppLoading />}><LegalPage type="privacy" /></Suspense></PublicPage>} />
            <Route path="/terms" element={<PublicPage><Suspense fallback={<AppLoading />}><LegalPage type="terms" /></Suspense></PublicPage>} />
            <Route path="/refund-policy" element={<PublicPage><Suspense fallback={<AppLoading />}><LegalPage type="refund-policy" /></Suspense></PublicPage>} />
            <Route path="/acceptable-use" element={<PublicPage><Suspense fallback={<AppLoading />}><LegalPage type="acceptable-use" /></Suspense></PublicPage>} />
            <Route path="/sms-terms" element={<PublicPage><Suspense fallback={<AppLoading />}><LegalPage type="sms-terms" /></Suspense></PublicPage>} />
            <Route path="/welcome" element={<AuthGuard><Suspense fallback={<AppLoading />}><Welcome /></Suspense></AuthGuard>} />
            <Route path="/" element={isAuthenticated ? <Navigate to={defaultWorkspacePath(tenant, user?.role)} replace /> : onAppHost ? <Navigate to="/login" replace /> : <PublicPage><LandingPage /></PublicPage>} />
            <Route path="/login" element={isAuthenticated ? <Navigate to={defaultWorkspacePath(tenant, user?.role)} replace /> : <Suspense fallback={<AppLoading />}><Login /></Suspense>} />
            <Route path="/signup/:licenseKey" element={<Suspense fallback={<AppLoading />}><InviteSignup /></Suspense>} />
            <Route path="/*" element={<AuthGuard><ProtectedLayout /></AuthGuard>} />
          </Routes>
        )}
      </AppErrorBoundary>
    </ThemeProvider>
  );
}
